import React, { useState, useEffect } from 'react';
import Modal from 'aldoo-ra/Modal';
import Tabs from 'aldoo-ra/Tabs';
import Input from 'aldoo-ra/Input';
import Typography from 'aldoo-ra/Typography';
import Button from 'aldoo-ra/Button';
import DatePicker from 'aldoo-ra/DatePicker';
import Tooltip from 'aldoo-ra/Tooltip';
import MediaLibrary from 'aldoo-ra/MediaLibrary';
import LocalizationItemEditor from 'aldoo-ra/Localization/localizaiton-item-editor';
import generateLocalizationKey from 'aldoo-ra/CMS/generate-localization-key';
import { url } from 'aldoo-ra/CMS/url';
import { LanguageIcon, PhotoIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { BREAKPOINTS } from '../constants.js';

const LocalizationButton = ({ fieldName, pageId, editor, openLocalization }) => {
  if (!pageId) return null;

  const isLocalized = editor?.page?.localization?.find(
    (item) => item.field === fieldName
  );

  return (
    <Tooltip content={isLocalized ? "Edit Localization" : "Add Localization"} position="top">
      <button
        onClick={() => openLocalization(fieldName)}
        className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
          isLocalized
            ? 'border-admin_primary text-admin_primary hover:bg-admin_primary hover:text-white'
            : 'border-admin_bordered text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary'
        }`}
        type="button"
      >
        <LanguageIcon className="w-5 h-5" />
      </button>
    </Tooltip>
  );
};

const FieldRow = ({ label, fieldName, children, hint, pageId, editor, openLocalization }) => {
  const isLocalized = React.useMemo(() => {
    return !!editor?.page?.localization?.find(
      (item) => item.field === fieldName
    );
  }, [editor?.page?.localization, fieldName]);

  return (
    <div className="grid grid-cols-[120px_1fr] gap-4 items-start py-3 border-b border-admin_bordered last:border-0">
      <div className="pt-2">
        <div className="flex items-center gap-2">
          <Typography variant="admin_label">{label}</Typography>
        </div>
        {isLocalized && (
            <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] rounded bg-admin_primary/10 text-admin_primary border border-admin_primary/20">
              Localized
            </span>
        )}
        {hint && (
          <Typography variant="admin_txt1_dim" className="text-[10px] mt-1 leading-tight">
            {hint}
          </Typography>
        )}
      </div>
      <div className="pt-1 flex gap-2 items-start">
        <div className="flex-1">
          {React.cloneElement(children, {
            disabled: children.props.disabled || isLocalized,
            readOnly: children.props.readOnly || isLocalized,
            className: children.props.className
          })}
        </div>
        {fieldName && (
            <LocalizationButton
            fieldName={fieldName}
            pageId={pageId}
            editor={editor}
            openLocalization={openLocalization}
            />
        )}
      </div>
    </div>
  );
};

const GeneralTab = ({ editor }) => {
  const handleChange = (field, value) => {
    if (field === 'slug') {
      const linkLabel = editor.content.name || value;
      editor.updateContent({
        slug: value,
        link: [{
          label: linkLabel,
          content: { url: value, type: 'page' }
        }]
      });
    } else if (field === 'name') {
      const currentSlug = editor.content.slug || editor.content.id || '';
      editor.updateContent({
        name: value,
        link: [{
          label: value,
          content: { url: currentSlug, type: 'page' }
        }]
      });
    } else {
      editor.updateContent({ [field]: value });
    }
  };

  const link = editor.content?.link?.[0];
  const linkContent = link?.content || link;
  const slug = linkContent?.url || editor.content?.slug || editor.content?.id || '';

  return (
    <div className="space-y-1">
      <FieldRow label="Name" hint="Display name">
        <Input
          variant="admin"
          value={editor.content?.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="Page name"
        />
      </FieldRow>
      <FieldRow label="Slug" hint="URL path">
        <Input
          variant="admin"
          value={slug}
          onChange={(e) => handleChange('slug', e.target.value)}
          placeholder="page-slug"
        />
      </FieldRow>
      <FieldRow label="ID" hint="Unique ID">
        <Input
          variant="admin"
          value={editor.content?.id || ''}
          disabled={!!editor.content?.id}
          onChange={(e) => handleChange('id', e.target.value)}
          placeholder="page-id"
        />
      </FieldRow>
      <FieldRow label="Description">
        <textarea
          className="w-full px-3 py-2 bg-admin_canvas border border-admin_bordered rounded-lg text-admin_text resize-none focus:outline-none focus:ring-2 focus:ring-admin_primary text-sm"
          rows={3}
          value={editor.content?.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Internal description..."
        />
      </FieldRow>
    </div>
  );
};

const EditorTab = ({ editor }) => {
    const handleChange = (field, value) => {
        editor.updateContent({ [field]: value });
    };

    const handleSettingChange = (key, value) => {
        const currentSettings = editor.content?.editor_settings || {};
        editor.updateContent({
            editor_settings: {
                ...currentSettings,
                [key]: value
            }
        });
    };

    const settings = editor.content?.editor_settings || {};

    const [breakpoints, setBreakpoints] = useState(() => {
        // Initialize with constants or saved values
        const saved = editor.content?.breakpoints;
        if (saved) return saved;
        
        // Convert BREAKPOINTS object to array for editing
        return Object.entries(BREAKPOINTS).map(([key, width]) => ({ key, width }));
    });

    // Update editor content when breakpoints change
    useEffect(() => {
        // Only save if different from constants
        editor.updateContent({ breakpoints });
    }, [breakpoints]);

    const handleBreakpointChange = (index, field, value) => {
        const newBreakpoints = [...breakpoints];
        newBreakpoints[index] = { ...newBreakpoints[index], [field]: value };
        setBreakpoints(newBreakpoints);
    };

    const handleAddBreakpoint = () => {
        setBreakpoints([...breakpoints, { key: 'new', width: 1600 }]);
    };

    const handleDeleteBreakpoint = (index) => {
        const newBreakpoints = breakpoints.filter((_, i) => i !== index);
        setBreakpoints(newBreakpoints);
    };

    return (
        <div className="space-y-4 pt-2">
            <div>
                <Typography variant="admin_label" className="mb-2">Canvas Size</Typography>
                <div className="flex gap-2">
                    <div className="flex-1">
                        <Typography variant="admin_txt1_dim" className="text-[10px] mb-1">Width</Typography>
                        <Input 
                            variant="admin" 
                            type="number" 
                            value={editor.content?.page_width || 1280} 
                            onChange={(e) => handleChange('page_width', parseInt(e.target.value) || 1280)} 
                        />
                    </div>
                    <div className="flex-1">
                        <Typography variant="admin_txt1_dim" className="text-[10px] mb-1">Height</Typography>
                        <Input 
                            variant="admin" 
                            type="number" 
                            value={editor.content?.page_height || 720} 
                            onChange={(e) => handleChange('page_height', parseInt(e.target.value) || 720)} 
                        />
                    </div>
                </div>
            </div>

            <div className="border-t border-admin_bordered pt-4">
                <Typography variant="admin_label" className="mb-2 flex items-center gap-2">
                   Snapping
                </Typography>
                
                <div className="space-y-3">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input 
                            type="checkbox" 
                            checked={settings.snapToObjects ?? true} 
                            onChange={(e) => handleSettingChange('snapToObjects', e.target.checked)}
                            className="rounded border-admin_bordered text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-xs text-admin_text">Snap to Objects</span>
                    </label>

                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input 
                                type="checkbox" 
                                checked={settings.snapToGrid ?? false} 
                                onChange={(e) => handleSettingChange('snapToGrid', e.target.checked)}
                                className="rounded border-admin_bordered text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-xs text-admin_text">Snap to Grid</span>
                        </label>
                        
                        {settings.snapToGrid && (
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] text-gray-500">Size:</span>
                                <Input 
                                    variant="admin" 
                                    type="number" 
                                    className="w-16 py-0.5 text-xs"
                                    value={settings.gridSize || 10} 
                                    onChange={(e) => handleSettingChange('gridSize', parseInt(e.target.value) || 10)} 
                                />
                                <span className="text-[10px] text-gray-500">px</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="border-t border-admin_bordered pt-4">
                <div className="flex items-center justify-between mb-2">
                    <Typography variant="admin_label">Breakpoints</Typography>
                    {/* <button onClick={handleAddBreakpoint} className="text-xs text-admin_primary hover:underline flex items-center gap-1">
                         <PlusIcon className="w-3 h-3" /> Add
                    </button> */}
                </div>
                
                <div className="border border-admin_bordered rounded-lg overflow-hidden">
                    <table className="w-full text-left text-xs">
                        <thead className="bg-admin_neutral_1/30 text-admin_txt1_dim">
                            <tr>
                                <th className="px-3 py-2 font-medium">Key</th>
                                <th className="px-3 py-2 font-medium">Min Width (px)</th>
                                {/* <th className="px-2 py-2 w-8"></th> */}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-admin_bordered">
                            {breakpoints.map((bp, idx) => (
                                <tr key={idx} className="bg-admin_canvas">
                                    <td className="px-3 py-2 text-admin_text font-mono">{bp.key}</td>
                                    <td className="px-3 py-2 text-admin_text_secondary">{bp.width}</td>
                                    {/* <td className="px-2 py-2 text-center">
                                        {bp.key !== 'default' && (
                                            <button onClick={() => handleDeleteBreakpoint(idx)} className="text-admin_text_secondary hover:text-admin_error">
                                                <TrashIcon className="w-3 h-3" />
                                            </button>
                                        )}
                                    </td> */}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Typography variant="admin_txt1_dim" className="text-[10px] mt-2">
                    Define the minimum width for each breakpoint. 'default' is the base style (0px).
                </Typography>
            </div>
        </div>
    );
};

const SEOTab = ({ editor }) => {
  const [localizationEditor, setLocalizationEditor] = useState({ isOpen: false, field: null, key: null });
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const pageId = editor.pageId;

  const handleChange = (field, value) => {
    const fieldMap = {
      'title': 'seo_title',
      'description': 'seo_description',
      'keywords': 'seo_keywords',
      'ogImage': 'seo_og_image',
    };
    editor.updateContent({ [fieldMap[field]]: value });
  };

  const getLocalizationKey = (fieldName) => {
    if (!pageId) return null;
    return generateLocalizationKey({
      type: 'AldooPage',
      fieldID: fieldName,
      instanceID: pageId,
    });
  };

  const openLocalization = async (fieldName) => {
    const key = getLocalizationKey(fieldName);
    if (!key) return;

    const isLocalized = editor.page?.localization?.find(item => item.field === fieldName);

    if (!isLocalized) {
      if (!editor.page.localization) editor.page.localization = [];
      editor.page.localization.push({ field: fieldName, localizationKey: key });

      try {
        const { SaveLocalizationItem } = await import('aldoo-ra/Localization/api');
        const fieldMap = {
          'seo_title': editor.content.seo_title,
          'seo_description': editor.content.seo_description,
          'seo_keywords': editor.content.seo_keywords,
          'seo_og_image': editor.content.seo_og_image
        };
        const currentValue = fieldMap[fieldName] || '';
        const fieldType = fieldName === 'seo_og_image' ? 'image' : 'text';

        await SaveLocalizationItem({
          key,
          language: 'en',
          content: currentValue,
          type: fieldType
        });
      } catch (error) {
        console.error('Failed to create initial localization entry:', error);
      }
    }

    setLocalizationEditor({ isOpen: true, field: fieldName, key });
  };

  return (
    <>
      <div className="space-y-1">
        <FieldRow label="Title" fieldName="seo_title" hint="SEO Title" pageId={pageId} editor={editor} openLocalization={openLocalization}>
          <Input
            variant="admin"
            value={editor.content?.seo_title || editor.content?.name || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="SEO Title"
          />
        </FieldRow>
        <FieldRow label="Description" fieldName="seo_description" hint="Meta description" pageId={pageId} editor={editor} openLocalization={openLocalization}>
          <textarea
            className="w-full px-3 py-2 bg-admin_canvas border border-admin_bordered rounded-lg text-admin_text resize-none focus:outline-none focus:ring-2 focus:ring-admin_primary text-sm"
            rows={3}
            value={editor.content?.seo_description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Meta description..."
          />
        </FieldRow>
        <FieldRow label="Keywords" fieldName="seo_keywords" hint="Comma separated" pageId={pageId} editor={editor} openLocalization={openLocalization}>
          <Input
            variant="admin"
            value={editor.content?.seo_keywords || ''}
            onChange={(e) => handleChange('keywords', e.target.value)}
            placeholder="keywords..."
          />
        </FieldRow>
        <FieldRow label="Social Image" fieldName="seo_og_image" pageId={pageId} editor={editor} openLocalization={openLocalization}>
            {editor.content?.seo_og_image ? (
                <div className="flex items-center gap-3">
                  <div className="relative group w-20 h-20 rounded-lg overflow-hidden border border-admin_bordered bg-admin_canvas">
                      <img src={url(editor.content.seo_og_image)} alt="OG" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button onClick={(e) => {e.stopPropagation(); handleChange('ogImage', '');}} className="p-1.5 bg-admin_error rounded-full text-white hover:bg-admin_error/80">
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </div>
                  </div>
                  <Button variant="admin_secondary" size="small" onClick={() => setIsMediaLibraryOpen(true)}>Change</Button>
                </div>
            ) : (
                <Button variant="admin_secondary" size="small" onClick={() => setIsMediaLibraryOpen(true)}><PhotoIcon className="w-4 h-4 mr-1"/>Select Image</Button>
            )}
        </FieldRow>
      </div>

      {isMediaLibraryOpen && (
        <MediaLibrary
          isOpen={isMediaLibraryOpen}
          onClose={() => setIsMediaLibraryOpen(false)}
          onFileSelect={(file) => { if(file) handleChange('ogImage', file.path || file.url); setIsMediaLibraryOpen(false); }}
          selectMode="single"
          fileType="image"
        />
      )}

      {localizationEditor.isOpen && (
        <LocalizationItemEditor
          parentObject={editor.page}
          isOpen={localizationEditor.isOpen}
          localizationKey={localizationEditor.key}
          onClose={() => setLocalizationEditor({ isOpen: false, field: null, key: null })}
          defaultValue={
              localizationEditor.field === 'seo_title' ? (editor.content.seo_title || editor.content.name) :
              localizationEditor.field === 'seo_description' ? editor.content.seo_description :
              localizationEditor.field === 'seo_keywords' ? editor.content.seo_keywords :
              editor.content.seo_og_image
          }
          onSave={async (item) => {
            const newValue = item.translations.en.content;
            editor.updateContent({ [localizationEditor.field]: newValue });
          }}
        />
      )}
    </>
  );
};

const PublishingTab = ({ editor }) => {
  const handleChange = (field, value) => {
    editor.updateContent({ [field]: value });
  };

  const status = editor.content?.status || 'draft';

  return (
    <div className="space-y-4 pt-2">
      <div>
          <Typography variant="admin_label" className="mb-2">Status</Typography>
          <div className="grid grid-cols-3 gap-2">
            {['draft', 'scheduled', 'published'].map(s => (
              <button
                key={s}
                onClick={() => handleChange('status', s)}
                className={`px-2 py-2 rounded text-xs font-semibold capitalize border transition-all ${
                  status === s
                    ? 'border-admin_primary bg-admin_primary/10 text-admin_primary'
                    : 'border-admin_bordered text-admin_text_secondary hover:bg-admin_neutral_1/10'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
      </div>

      {status === 'scheduled' && (
        <div>
            <Typography variant="admin_label" className="mb-2">Publish Date</Typography>
            <DatePicker
              variant="admin"
              value={editor.content?.publish_date ? new Date(editor.content.publish_date) : null}
              onChange={(date) => handleChange('publish_date', date?.toISOString() || '')}
              dateFormat="MMM DD, YYYY"
            />
        </div>
      )}
    </div>
  );
};

const PageSettingsDialog = ({ isOpen, onClose, editor }) => {
  const [activeTab, setActiveTab] = useState('general');

  const tabs = [
    { value: 'general', label: 'General' },
    { value: 'editor', label: 'Editor' },
    { value: 'seo', label: 'SEO' },
    { value: 'publishing', label: 'Publishing' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'general': return <GeneralTab editor={editor} />;
      case 'editor': return <EditorTab editor={editor} />;
      case 'seo': return <SEOTab editor={editor} />;
      case 'publishing': return <PublishingTab editor={editor} />;
      default: return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_compact"
      title="Page Settings"
      closeOnEscape
    >
      <div className="h-full flex flex-col">
        <Tabs 
            tabs={tabs} 
            value={activeTab} 
            onChange={setActiveTab} 
            variant="admin" 
            className="flex-shrink-0" 
        />
        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
            {renderContent()}
        </div>
        <div className="p-4 border-t border-admin_bordered flex justify-end flex-shrink-0">
            <Button onClick={onClose} variant="admin_primary">Done</Button>
        </div>
      </div>
    </Modal>
  );
};

export default PageSettingsDialog;